import type { Family } from '../game/types'

// Adresse du MonsterNFT déployé sur Base Sepolia (contracts/scripts/deploy.js).
// Vide → l'app tourne en mode démo pur (localStorage).
export const MONSTER_CONTRACT = (import.meta.env.VITE_MONSTER_CONTRACT ?? '') as `0x${string}`

export const isOnchainConfigured = /^0x[0-9a-fA-F]{40}$/.test(MONSTER_CONTRACT)

// Arène classée: contrat de classement + résolveur qui rejoue les combats.
export const ARENA_CONTRACT = (import.meta.env.VITE_ARENA_CONTRACT ?? '') as `0x${string}`
export const isArenaConfigured = /^0x[0-9a-fA-F]{40}$/.test(ARENA_CONTRACT)
export const RESOLVER_URL: string = import.meta.env.VITE_RESOLVER_URL ?? 'http://localhost:8787'

// Essence + Forge: la ressource minée en gagnant des combats d'arène.
export const ESSENCE_CONTRACT = (import.meta.env.VITE_ESSENCE_CONTRACT ?? '') as `0x${string}`
export const isEssenceConfigured = /^0x[0-9a-fA-F]{40}$/.test(ESSENCE_CONTRACT)

// Marketplace: escrow monstres + essences, frais en bps.
export const MARKET_CONTRACT = (import.meta.env.VITE_MARKET_CONTRACT ?? '') as `0x${string}`
export const isMarketConfigured = /^0x[0-9a-fA-F]{40}$/.test(MARKET_CONTRACT)

// Duels avec mise: auto-battles déterministes arbitrés par le résolveur.
export const DUEL_CONTRACT = (import.meta.env.VITE_DUEL_CONTRACT ?? '') as `0x${string}`
export const isDuelConfigured = /^0x[0-9a-fA-F]{40}$/.test(DUEL_CONTRACT)

// --- Câblage V2 (phase 5, prêt-au-déploiement) ---
// Drapeau maître: passe l'app en mode contrats V2 (struct Duel 9 champs,
// mint payable, retryHatch, rareté garantie…). Reste FALSE tant que la suite
// V2 n'est pas déployée → l'app parle aux contrats V1 déployés sans rien casser.
export const CONTRACTS_V2 = import.meta.env.VITE_CONTRACTS_V2 === '1'
// Recycleur/Daily V2 exposent guaranteedRarityOf — lus pour afficher la rareté
// FORGÉE (hors classé; en classé la rareté est normalisée donc sans effet).
export const RECYCLER_CONTRACT = (import.meta.env.VITE_RECYCLER_CONTRACT ?? '') as `0x${string}`
export const DAILY_CONTRACT = (import.meta.env.VITE_DAILY_CONTRACT ?? '') as `0x${string}`
export const isRecyclerConfigured = /^0x[0-9a-fA-F]{40}$/.test(RECYCLER_CONTRACT)
export const isDailyConfigured = /^0x[0-9a-fA-F]{40}$/.test(DAILY_CONTRACT)

export interface LootVoucher {
  recipient: `0x${string}`
  family: number
  amount: number
  battleHash: `0x${string}`
  signature: `0x${string}`
}

// Doit rester aligné avec l'ordre des familles dans MonsterNFT.sol.
export const FAMILY_BY_INDEX: Family[] = ['bitcoin', 'ethereum', 'base', 'defi', 'meme', 'stable']

export const FAMILY_INDEX = Object.fromEntries(
  FAMILY_BY_INDEX.map((f, i) => [f, i]),
) as Record<Family, number>

export const monsterAbi = [
  {
    type: 'function',
    name: 'mintMonster',
    // payable: compatible V1 (nonpayable on-chain accepte value=0) ET V2 (prix).
    // On envoie value=mintPrice (0 en V1). Voir web3/v2.ts::mintValue.
    stateMutability: 'payable',
    inputs: [{ name: 'family', type: 'uint8' }],
    outputs: [{ name: 'tokenId', type: 'uint256' }],
  },
  // --- V2 uniquement (inertes contre V1: lues en try/catch, appelées si V2) ---
  { type: 'function', name: 'mintPrice', stateMutability: 'view', inputs: [], outputs: [{ name: '', type: 'uint256' }] },
  { type: 'function', name: 'mintPaused', stateMutability: 'view', inputs: [], outputs: [{ name: '', type: 'bool' }] },
  { type: 'function', name: 'maxPublicPerWallet', stateMutability: 'view', inputs: [], outputs: [{ name: '', type: 'uint256' }] },
  { type: 'function', name: 'retryHatch', stateMutability: 'nonpayable', inputs: [{ name: 'tokenId', type: 'uint256' }], outputs: [] },
  { type: 'function', name: 'burn', stateMutability: 'nonpayable', inputs: [{ name: 'tokenId', type: 'uint256' }], outputs: [] },
  {
    type: 'function',
    name: 'monstersOfOwner',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [
      { name: 'ids', type: 'uint256[]' },
      { name: 'families', type: 'uint8[]' },
      { name: 'seeds', type: 'bytes32[]' },
      { name: 'wins', type: 'uint32[]' },
      { name: 'losses', type: 'uint32[]' },
    ],
  },
  {
    type: 'function',
    name: 'recordBattle',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'tokenId', type: 'uint256' },
      { name: 'won', type: 'bool' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'totalSupply',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'tokenByIndex',
    stateMutability: 'view',
    inputs: [{ name: 'index', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'getMonster',
    stateMutability: 'view',
    inputs: [{ name: 'tokenId', type: 'uint256' }],
    outputs: [
      { name: 'family', type: 'uint8' },
      { name: 'seed', type: 'bytes32' },
      { name: 'mintedAt', type: 'uint64' },
    ],
  },
  {
    type: 'function',
    name: 'setApprovalForAll',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'isApprovedForAll',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    type: 'function',
    name: 'seasonInfo',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'current', type: 'uint16' },
      { name: 'cap', type: 'uint256' },
      { name: 'minted', type: 'uint256' },
    ],
  },
  {
    type: 'event',
    name: 'MonsterHatched',
    inputs: [
      { name: 'tokenId', type: 'uint256', indexed: true },
      { name: 'seed', type: 'bytes32', indexed: false },
    ],
  },
  {
    type: 'event',
    name: 'MonsterMinted',
    inputs: [
      { name: 'owner', type: 'address', indexed: true },
      { name: 'tokenId', type: 'uint256', indexed: true },
      { name: 'family', type: 'uint8', indexed: false },
      { name: 'seed', type: 'bytes32', indexed: false },
    ],
  },
] as const

export const arenaAbi = [
  {
    type: 'function',
    name: 'submitResult',
    stateMutability: 'nonpayable',
    inputs: [
      {
        name: 'result',
        type: 'tuple',
        components: [
          { name: 'playerToken', type: 'uint256' },
          { name: 'opponentToken', type: 'uint256' },
          { name: 'playerWon', type: 'bool' },
          { name: 'battleHash', type: 'bytes32' },
        ],
      },
      { name: 'signature', type: 'bytes' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'ratingsPage',
    stateMutability: 'view',
    inputs: [
      { name: 'offset', type: 'uint256' },
      { name: 'count', type: 'uint256' },
    ],
    outputs: [
      { name: 'ids', type: 'uint256[]' },
      { name: 'ratings', type: 'uint32[]' },
      { name: 'wins', type: 'uint32[]' },
      { name: 'losses', type: 'uint32[]' },
    ],
  },
  {
    type: 'function',
    name: 'participantCount',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

export const essenceAbi = [
  {
    type: 'function',
    name: 'claimLoot',
    stateMutability: 'nonpayable',
    inputs: [
      {
        name: 'v',
        type: 'tuple',
        components: [
          { name: 'recipient', type: 'address' },
          { name: 'family', type: 'uint8' },
          { name: 'amount', type: 'uint256' },
          { name: 'battleHash', type: 'bytes32' },
        ],
      },
      { name: 'signature', type: 'bytes' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'forgeMonster',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'family', type: 'uint8' }],
    outputs: [{ name: 'tokenId', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'balanceOfBatch',
    stateMutability: 'view',
    inputs: [
      { name: 'accounts', type: 'address[]' },
      { name: 'ids', type: 'uint256[]' },
    ],
    outputs: [{ name: '', type: 'uint256[]' }],
  },
  {
    type: 'function',
    name: 'setApprovalForAll',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'isApprovedForAll',
    stateMutability: 'view',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const

// La V1 déployée expose un struct Duel à 8 champs; la V2 durcie en a 9
// (acceptedAt, base du timeout cancelStale). Décoder avec le mauvais nombre de
// champs corrompt la liste → on choisit selon le drapeau CONTRACTS_V2.
const DUEL_COMPONENTS_V1 = [
  { name: 'challenger', type: 'address' },
  { name: 'challengerToken', type: 'uint256' },
  { name: 'opponent', type: 'address' },
  { name: 'opponentToken', type: 'uint256' },
  { name: 'family', type: 'uint8' },
  { name: 'stake', type: 'uint256' },
  { name: 'battleSeed', type: 'bytes32' },
  { name: 'status', type: 'uint8' },
] as const

const DUEL_COMPONENTS_V2 = [
  ...DUEL_COMPONENTS_V1,
  { name: 'acceptedAt', type: 'uint64' },
] as const

const DUEL_COMPONENTS = CONTRACTS_V2 ? DUEL_COMPONENTS_V2 : DUEL_COMPONENTS_V1

export const duelAbi = [
  {
    type: 'function',
    name: 'create',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'tokenId', type: 'uint256' },
      { name: 'family', type: 'uint8' },
      { name: 'stake', type: 'uint256' },
    ],
    outputs: [{ name: 'id', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'accept',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'id', type: 'uint256' },
      { name: 'tokenId', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    type: 'function',
    name: 'cancel',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'id', type: 'uint256' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'resolve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'id', type: 'uint256' },
      { name: 'outcome', type: 'uint8' },
      { name: 'battleHash', type: 'bytes32' },
      { name: 'signature', type: 'bytes' },
    ],
    outputs: [],
  },
  // --- V2 uniquement (KetDuel durci): filet de vivacité + pull-payment ---
  { type: 'function', name: 'cancelStale', stateMutability: 'nonpayable', inputs: [{ name: 'id', type: 'uint256' }], outputs: [] },
  { type: 'function', name: 'claimPayout', stateMutability: 'nonpayable', inputs: [{ name: 'family', type: 'uint8' }], outputs: [] },
  { type: 'function', name: 'pendingPayouts', stateMutability: 'view', inputs: [{ name: 'to', type: 'address' }, { name: 'family', type: 'uint8' }], outputs: [{ name: '', type: 'uint256' }] },
  {
    type: 'function',
    name: 'rakeBps',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint16' }],
  },
  {
    type: 'function',
    name: 'duelsPage',
    stateMutability: 'view',
    inputs: [
      { name: 'offset', type: 'uint256' },
      { name: 'count', type: 'uint256' },
    ],
    outputs: [
      { name: 'ids', type: 'uint256[]' },
      { name: 'items', type: 'tuple[]', components: DUEL_COMPONENTS },
    ],
  },
] as const

const LISTING_COMPONENTS = [
  { name: 'seller', type: 'address' },
  { name: 'kind', type: 'uint8' },
  { name: 'tokenId', type: 'uint256' },
  { name: 'amount', type: 'uint256' },
  { name: 'price', type: 'uint256' },
  { name: 'active', type: 'bool' },
] as const

export const marketAbi = [
  {
    type: 'function',
    name: 'listMonster',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'tokenId', type: 'uint256' },
      { name: 'price', type: 'uint256' },
    ],
    outputs: [{ name: 'id', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'listEssence',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'family', type: 'uint8' },
      { name: 'amount', type: 'uint256' },
      { name: 'price', type: 'uint256' },
    ],
    outputs: [{ name: 'id', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'cancel',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'id', type: 'uint256' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'buy',
    stateMutability: 'payable',
    inputs: [{ name: 'id', type: 'uint256' }],
    outputs: [],
  },
  {
    type: 'function',
    name: 'feeBps',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint16' }],
  },
  {
    type: 'function',
    name: 'listingCount',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'listingsPage',
    stateMutability: 'view',
    inputs: [
      { name: 'offset', type: 'uint256' },
      { name: 'count', type: 'uint256' },
    ],
    outputs: [
      { name: 'ids', type: 'uint256[]' },
      { name: 'items', type: 'tuple[]', components: LISTING_COMPONENTS },
    ],
  },
] as const

// Recycleur/Daily V2: rareté GARANTIE d'un token forgé (renvoie forged, rarity).
// Lue hors classé pour afficher/appliquer la bonne rareté à la place du seed.
export const guaranteedRarityAbi = [
  {
    type: 'function',
    name: 'guaranteedRarityOf',
    stateMutability: 'view',
    inputs: [{ name: 'tokenId', type: 'uint256' }],
    outputs: [
      { name: 'forged', type: 'bool' },
      { name: 'rarity', type: 'uint8' },
    ],
  },
] as const
